import { useCallback, useEffect, useMemo, useRef, type MutableRefObject, type RefObject } from 'react';
import { useAnalytics } from '../../../../core/analytics';
import { useBetaFeature } from '../../../../core/beta';
import type { DatabaseEngineId } from '../../../../core/engines';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import type { QueryViewMode } from '../../core/types/workspaceTypes';
import { positionAtOffset } from '../../visual-query-editor/inline/inlineModelAdapter';
import { sliceActiveStatement } from '../../visual-query-editor/execute/sliceActiveStatement';
import { visualStatementRun, type VisualStatementRun } from '../../visual-query-editor/execute/visualStatementRun';
import { useVqeStore } from '../../visual-query-editor/store/vqeStore';
import { classifyTabQuery, vqeParseOptionsFor } from '../../visual-query-editor/workspace/classifyTabQuery';
import { extractSubqueries } from '../../visual-query-editor/render/select/extractSubqueries';
import type { ParseOptions } from '../../../sql-grammar/model';
import type { DrillEntry } from '../../visual-query-editor/types';
import type { QueryEditorHandle } from '../components/QueryEditor';
import { cursorStatementOffset } from '../utils/cursorStatementOffsets';
import { rebaseStatementAnchor } from './rebaseStatementAnchor';

/** Fixed, low-cardinality entry points of the SQL | Visual switch (`feature:vqe_mode_toggle`). */
export type QueryViewModeSource =
  | 'query-toolbar'
  | 'toolbar'
  | 'shortcut'
  | 'palette'
  | 'compact-promote'
  | 'edit-as-sql';

/** A subquery frame the compact editor was drilled into (buffer offsets). */
export type QueryViewDrillFrame = { readonly bodyStart: number; readonly bodyEnd: number; readonly label: string };

export type QueryViewModeTarget = {
  /** Visual: show the statement containing this offset (default: the caret's). */
  readonly statementOffset?: number;
  /** Visual: keep the compact editor's drill, when it still fits the statement. */
  readonly drill?: readonly QueryViewDrillFrame[];
  /** SQL: put the caret here (a parse error) instead of selecting the statement. */
  readonly errorOffset?: number | null;
};

type UseQueryViewModeParams = {
  tabId: string;
  viewMode: QueryViewMode | undefined;
  visualStatementOffset: number | null | undefined;
  engineId: DatabaseEngineId;
  /** The tab's debounced buffer (what the Visual view renders). */
  query: string;
  editorRef: RefObject<QueryEditorHandle | null>;
  queryRef: MutableRefObject<string>;
  flushQueryDebounced: { flush: () => void };
  /** Close the SQL view's editor-anchored overlays (compact popover, peek, table picker). */
  closeSqlOverlays: () => void;
};

/**
 * The Query Editor tab's SQL | Visual view. Both views show ONE document:
 * Monaco stays mounted under the Visual view, whose edits go through Monaco's
 * undo stack. This hook owns the switch and the Visual view's statement anchor
 * (`visualStatementOffset`, the shown statement's start offset, persisted with
 * the tab):
 *  - entering Visual shows the statement under the caret (read live);
 *  - every buffer change while Visual shows rebases the anchor from Monaco's
 *    change ranges (`rebaseStatementAnchor`), in the same task as the buffer;
 *  - leaving Visual reveals + selects the shown statement (or the caret at a
 *    parse error) in the editor;
 *  - `resolveVisualRun` is what Execute / F5 / Cmd/Ctrl+Enter run while Visual
 *    shows — the shown statement, sliced from the LIVE buffer.
 * The Visual view is a `visual-query-editor` beta surface: with the beta off
 * a persisted `'visual'` renders SQL.
 */
export function useQueryViewMode({
  tabId,
  viewMode,
  visualStatementOffset,
  engineId,
  query,
  editorRef,
  queryRef,
  flushQueryDebounced,
  closeSqlOverlays,
}: UseQueryViewModeParams) {
  const { track } = useAnalytics();
  const vqeEnabled = useBetaFeature('visual-query-editor');
  const isVisual = vqeEnabled && viewMode === 'visual';
  const options = useMemo(() => vqeParseOptionsFor(engineId), [engineId]);

  // Synchronous mirrors: the change handler and execute paths run between
  // renders (a visual edit and its flush land in one event).
  const isVisualRef = useRef(isVisual);
  isVisualRef.current = isVisual;
  const anchorRef = useRef<number | null>(visualStatementOffset ?? null);
  anchorRef.current = visualStatementOffset ?? null;
  const lastTextRef = useRef<string | null>(null);

  const readLive = useCallback(() => editorRef.current?.getValue() ?? queryRef.current, [editorRef, queryRef]);
  const classifyLive = useCallback(
    (text: string) => (text.trim() ? classifyTabQuery(text, options, anchorRef.current ?? undefined) : null),
    [options],
  );
  const statementStarts = useCallback(
    (text: string) => classifyTabQuery(text, options).buffer.documentStructure.statements.map((s) => s.range.startOffset),
    [options],
  );

  const setAnchor = useCallback(
    (offset: number) => {
      anchorRef.current = offset;
      useWorkspaceStore.getState().updateTabContent(tabId, { visualStatementOffset: offset });
    },
    [tabId],
  );

  // While the Visual view shows, follow every buffer change with Monaco's
  // own change ranges (`rebaseStatementAnchor`). The subscription waits for a
  // restored tab's editor to mount. Published in the same task as the
  // buffer flush, so query + anchor land in one render.
  useEffect(() => {
    if (!isVisual) return undefined;
    let dispose: (() => void) | undefined;
    let retry: ReturnType<typeof setTimeout> | undefined;
    const attach = () => {
      const editor = editorRef.current;
      // The handle exists before Monaco has mounted (a restored tab): wait for
      // a model, or the subscription would be a silent no-op.
      if (!editor?.onDidChangeContent || editor.getModelVersionId() === null) {
        retry = setTimeout(attach, 100);
        return;
      }
      lastTextRef.current = editor.getValue() ?? queryRef.current;
      dispose = editor.onDidChangeContent((event) => {
        const next = editor.getValue() ?? '';
        const previous = lastTextRef.current ?? next;
        lastTextRef.current = next;
        const anchor = anchorRef.current;
        if (anchor === null || previous === next) return;
        const rebased = rebaseStatementAnchor(anchor, event, previous, next, statementStarts);
        if (rebased !== anchor) setAnchor(rebased);
      });
    };
    attach();
    return () => {
      if (retry) clearTimeout(retry);
      dispose?.();
    };
  }, [isVisual, editorRef, queryRef, statementStarts, setAnchor]);

  const setViewMode = useCallback(
    (mode: QueryViewMode, source: QueryViewModeSource, target: QueryViewModeTarget = {}) => {
      if (mode === 'visual') {
        if (!vqeEnabled) return;
        // Entering the view it already shows changes nothing — More ▾ must not
        // move a Visual view off the statement picked there — unless the
        // caller names a statement (the compact editor's promotion).
        if (isVisualRef.current && target.statementOffset === undefined) return;
        flushQueryDebounced.flush();
        const text = readLive();
        const caret = target.statementOffset ?? cursorStatementOffset(tabId) ?? editorRef.current?.getCursorOffset() ?? 0;
        const starts = text.trim() ? statementStarts(text) : [];
        const anchor = [...starts].reverse().find((start) => start <= caret) ?? starts[0] ?? 0;
        lastTextRef.current = text;
        isVisualRef.current = true;
        anchorRef.current = anchor;
        closeSqlOverlays();
        if (!isVisual) track('feature:vqe_mode_toggle', { mode, source });
        handOverDrill(tabId, text, anchor, options, target.drill);
        useWorkspaceStore.getState().updateTabContent(tabId, { viewMode: 'visual', visualStatementOffset: anchor });
        return;
      }
      if (!isVisualRef.current) return;
      const text = readLive();
      const statement = classifyLive(text)?.statement ?? null;
      isVisualRef.current = false;
      useVqeStore.getState().clearDrill(tabId);
      track('feature:vqe_mode_toggle', { mode, source });
      useWorkspaceStore.getState().updateTabContent(tabId, { viewMode: 'sql', visualStatementOffset: null });
      const errorOffset = target.errorOffset ?? null;
      const start = errorOffset ?? statement?.range.startOffset ?? null;
      const end = errorOffset ?? (statement ? statement.range.startOffset + sliceActiveStatement(text, statement).length : null);
      // Two frames: the editor is shown again on the next commit, and Monaco
      // lays itself out (automaticLayout) only after that frame's layout — a
      // reveal before it would scroll a zero-height viewport. Reveal first:
      // it moves the caret, which would collapse a selection set before it.
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          const editor = editorRef.current;
          if (!editor) return;
          if (start !== null && end !== null) {
            const from = positionAtOffset(text, start);
            const to = positionAtOffset(text, end);
            editor.revealLine(from.lineNumber, from.column);
            editor.setSelectionRange({
              startLineNumber: from.lineNumber,
              startColumn: from.column,
              endLineNumber: to.lineNumber,
              endColumn: to.column,
            });
          }
          editor.focus();
        }),
      );
    },
    [vqeEnabled, isVisual, flushQueryDebounced, readLive, classifyLive, statementStarts, closeSqlOverlays, editorRef, tabId, track, options],
  );

  const resolveVisualRun = useCallback((): VisualStatementRun | null => {
    if (!isVisualRef.current) return null;
    const text = readLive();
    const classification = classifyLive(text);
    return visualStatementRun(text, classification?.statement ?? null, classification?.renderMode ?? null, engineId);
  }, [readLive, classifyLive, engineId]);

  // The shown statement's text, for the results pane's stale notice.
  const shownStatementSql = useMemo(() => {
    if (!isVisual) return null;
    const classification = query.trim() ? classifyTabQuery(query, options, visualStatementOffset ?? undefined) : null;
    return sliceActiveStatement(query, classification?.statement ?? null);
  }, [isVisual, query, options, visualStatementOffset]);

  return {
    isVisual,
    isVisualRef,
    statementOffset: visualStatementOffset ?? null,
    setViewMode,
    selectStatement: setAnchor,
    resolveVisualRun,
    shownStatementSql,
  };
}

/**
 * The compact editor's "Open full editor" keeps its drill: the frames become
 * the tab's Visual-view drill trail — replacing any earlier trail of the tab
 * in one store write — but only when each one is still a real subquery of the
 * frame before it (the first one of the shown statement), found by the same
 * lexical scan the cards use, so an offset that now points into a string or a
 * comment is refused; ends are re-derived from the live text. Otherwise the
 * view starts at the statement's top level.
 */
export function handOverDrill(
  tabId: string,
  text: string,
  anchor: number,
  options: ParseOptions,
  frames: readonly QueryViewDrillFrame[] | undefined,
): void {
  const entries: DrillEntry[] = [];
  const statement = frames?.length && text.trim() ? classifyTabQuery(text, options, anchor).statement : null;
  if (statement && frames) {
    let outerStart = statement.range.startOffset;
    let outerEnd = statement.range.endOffset;
    for (const frame of frames) {
      // A frame may start right after its `(` (CTE bodies) or at the body's
      // first token (subquery chips) — either way, inside that paren group.
      const body = extractSubqueries(text, outerStart, outerEnd, options).find(
        (q) => q.parenStart < frame.bodyStart && frame.bodyStart <= q.bodyStart,
      );
      if (!body) {
        entries.length = 0;
        break;
      }
      entries.push({ tabId, parentStatementId: statement.id, bodyStart: frame.bodyStart, bodyEnd: body.bodyEnd, label: frame.label });
      outerStart = body.bodyStart;
      outerEnd = body.bodyEnd;
    }
  }
  useVqeStore.setState((state) => ({
    drillStack: [...state.drillStack.filter((entry) => entry.tabId !== tabId), ...entries],
  }));
}
